import React, { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

const DEBUG_API = "https://recon.jcinformatics.com/debug_request.php";

const PrivateRoute = () => {
  const [isAuth, setIsAuth] = useState(null); // null=loading, true/false
  const location = useLocation();
  const navigate = useNavigate();

  // Evita setState cuando el componente ya no está montado
  const aliveRef = useRef(true);

  const publicRoutes = useMemo(() => ["/login", "/seguimientoparte/"], []);

  useEffect(() => {
    aliveRef.current = true;

    const run = async () => {
      console.group("🔐 PrivateRoute DEBUG");
      console.log("📍 pathname:", location.pathname);

      try {
        // 1) Rutas públicas: no validar token
        if (publicRoutes.some((r) => location.pathname.startsWith(r))) {
          console.log("✅ Ruta pública");
          if (aliveRef.current) setIsAuth(true);
          return;
        }

        const token = localStorage.getItem("accessToken");
        console.log("🔑 accessToken:", token);

        if (!token) {
          console.warn("❌ No hay token");
          localStorage.clear();
          if (aliveRef.current) setIsAuth(false);
          return;
        }

        // 2) Petición al backend (con AbortController)
        const controller = new AbortController();

        const res = await fetch(DEBUG_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            token,
            from: "PrivateRoute",
            path: location.pathname,
          }),
          signal: controller.signal,
        });

        if (res.status === 401) {
          console.warn("❌ Backend responde 401 → limpiar sesión y login");
          localStorage.clear();
          if (aliveRef.current) setIsAuth(false);

          // Importante: replace para no volver con "atrás" a una ruta privada
          navigate("/login", { replace: true, state: { from: location } });
          return;
        }

        if (!res.ok) {
          // Si el backend cae o devuelve 500/403/etc, decide tu política:
          // aquí lo tratamos como NO autenticado para evitar acceso.
          const txt = await res.text().catch(() => "");
          console.warn("⚠️ Respuesta no OK:", res.status, txt);
          if (aliveRef.current) setIsAuth(false);
          return;
        }

        const data = await res.json().catch(() => ({}));
        console.log("📡 RESPUESTA debug_request.php:", data);

        // Si tu endpoint devuelve success=false, puedes forzar logout:
        // if (data?.success === false) { ... }
        if (aliveRef.current) setIsAuth(true);
      } catch (err) {
        // Si fue abortado por un cambio rápido de ruta, no hacemos nada
        if (err?.name === "AbortError") return;

        console.error("❌ Error sesión:", err?.message || err);
        // Si hay error de red, por seguridad: no auth
        if (aliveRef.current) setIsAuth(false);
      } finally {
        console.groupEnd();
      }
    };

    run();

    return () => {
      aliveRef.current = false;
    };
  }, [location.pathname, navigate, publicRoutes, location]);

  // Loader mientras comprobamos
  if (isAuth === null) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: 40 }}>
        <CircularProgress />
      </div>
    );
  }

  // ✅ Si está autenticado, deja pasar
  // ❌ Si no, redirige a login (guardando de dónde venía)
  return isAuth ? (
    <Outlet />
  ) : (
    <Navigate to="/login" replace state={{ from: location }} />
  );
};

export default PrivateRoute;
